import { FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Avatar, Button, Card, DatePicker, Form, Input, Space, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useState } from 'react';
import CopyableText from '../../components/copyable-text';
import DeviceDetails from '../../components/device-details';
import axios from '../../config/axios';
import AppLayout from '../../layouts/app.layout';
import { Device } from '../../types/device.types';
import { RetailerMinimal } from '../../types/retailers.types';
import { UserMinimal } from '../../types/user.types';
import { formatNumber, handleAPIError } from '../../utils/helpers';
import RetailerPayout from '../../components/retailer-payout';

const { RangePicker } = DatePicker;

interface FilterValues {
	status: string | null;
	phone_number: string | null;
	retailer: number | null;
	user: number | null;
	date_range: [dayjs.Dayjs, dayjs.Dayjs] | null;
}

const DeviceReportV1 = () => {
	const [form] = Form.useForm<FilterValues>();
	const [paginationConfig, setPaginationConfig] = useState({
		page: 1,
		pageSize: 10,
	});
	const [count, setCount] = useState(0);
	const [filters, setFilters] = useState<FilterValues>({
		status: null,
		phone_number: null,
		retailer: null,
		user: null,
		date_range: null,
	});
	const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
	const [selectedPayoutDevice, setSelectedPayoutDevice] = useState<Device | null>(null);

	const { data, error, isLoading } = useQuery({
		queryKey: ['devices', paginationConfig.page, paginationConfig.pageSize, filters],
		queryFn: async () => {
			const params = {
				page: paginationConfig.page,
				limit: paginationConfig.pageSize,
				status: 'approved',
				phone_number: filters.phone_number,
				retailer: filters.retailer,
				user: filters.user,
				start: filters.date_range ? filters.date_range[0].format('YYYY-MM-DD') : null,
				end: filters.date_range ? filters.date_range[1].format('YYYY-MM-DD') : null,
			};

			const response = await axios.get('/admin/devices', {
				withCredentials: true,
				params,
			});
			setCount(response.data.count);
			return response.data;
		},
	});

	if (error) {
		handleAPIError(error);
	}

	const devices: Device[] = data?.results || [];

	const columns: ColumnsType<Device> = [
		{
			title: 'ID',
			dataIndex: 'id',
			key: 'id',
		},
		{
			title: 'User',
			dataIndex: 'user',
			key: 'user',
			render: (user: UserMinimal) => (
				<div className="flex items-center gap-2">
					<Avatar src={user.dp} />
					<div>
						<span className="text-sm font-medium uppercase">
							{user.first_name} {user.last_name}
						</span>
						<CopyableText text={user.phone_number}>
							<a
								className="text-sm text-gray-500"
								href={`tel:+${user.phone_number}`}
							>
								{user.phone_number}
							</a>
						</CopyableText>
					</div>
				</div>
			),
		},
		{
			title: 'Retailer',
			dataIndex: 'retailer',
			key: 'retailer',
			render: (retailer: RetailerMinimal) => (
				<div>
					<span className="text-sm font-medium uppercase">{retailer.business_name}</span>
					<CopyableText
						text={retailer.vendor_id}
						className="text-sm text-gray-500"
					/>
				</div>
			),
		},
		{
			title: 'Sold By',
			dataIndex: 'sold_by',
			key: 'sold_by',
			render: (sold_by: RetailerMinimal | null) => (
				<div>
					{sold_by ? (
						<div className="flex items-center gap-2">
							<div>
								<span className="text-sm font-medium uppercase">{sold_by.business_name}</span>
								<CopyableText
									text={sold_by.vendor_id}
									className="text-sm text-gray-500"
								/>
							</div>
						</div>
					) : (
						<span>N/A</span>
					)}
				</div>
			),
		},
		{
			title: 'Amount',
			dataIndex: 'phone_amount',
			key: 'phone_amount',
			render: (phone_amount: string) => `${formatNumber(phone_amount)}`,
		},
		{
			title: 'Cash In Hand',
			dataIndex: 'cash_in_hand',
			key: 'cash_in_hand',
			render: (cash_in_hand: string) => `${formatNumber(cash_in_hand)}`,
		},
		{
			title: 'Paid to Retailer',
			dataIndex: 'zf_paid_to_retailer',
			key: 'zf_paid_to_retailer',
			render: (zf_paid_to_retailer: string) => `${formatNumber(zf_paid_to_retailer ?? 0)}`,
		},
		{
			title: 'Boarding Fees',
			dataIndex: 'zazu_fee',
			key: 'zazu_fee',
			render: (zazu_fee: string) => `${formatNumber(zazu_fee)}`,
		},
		{
			title: 'To be paid',
			dataIndex: 'total_installment_amount',
			key: 'total_installment_amount',
			render: (total_installment_amount: string) => `${formatNumber(total_installment_amount)}`,
		},
		{
			title: 'Paid',
			dataIndex: 'total_amount_paid',
			key: 'total_amount_paid',
			render: (total_amount_paid: string, device: Device) =>
				`${formatNumber(parseInt(total_amount_paid) - parseInt(device.zazu_fee) - parseInt(device.cash_in_hand))}`,
		},
		{
			title: 'Balance',
			dataIndex: 'total_amount_to_be_paid',
			key: 'total_amount_to_be_paid',
			render: (total_amount_to_be_paid: string, device: Device) =>
				`${formatNumber(Math.round(parseFloat(total_amount_to_be_paid) - parseFloat(device.total_amount_paid)))}`,
		},
		{
			title: 'Plan',
			dataIndex: 'payment_plan',
			key: 'payment_plan',
			render: (payment_plan: string) => `${payment_plan.toUpperCase()}`,
		},
		{
			title: 'Period',
			dataIndex: 'duration',
			key: 'duration',
			render: (duration: number) => `${duration}`,
		},
		{
			title: 'ODP',
			dataIndex: 'has_overdue_payment',
			key: 'has_overdue_payment',
			render: (has_overdue_payment: boolean, device: Device) => (
				<div>
					<Tag color={has_overdue_payment ? 'red' : 'green'}>{has_overdue_payment ? 'Yes' : 'No'}</Tag>
					{device.penalty_amount && has_overdue_payment && (
						<div className="mt-1 text-xs text-red-500">Penalty: {formatNumber(Math.round(parseFloat(device.penalty_amount)))} UGX</div>
					)}
				</div>
			),
		},
		{
			title: 'PO',
			dataIndex: 'funding_type',
			key: 'funding_type',
			render: (_: string, device: Device) => (
				<Button
					onClick={() => setSelectedPayoutDevice(device)}
					disabled={!eligibleForPayout(device)}
				>
					PO
				</Button>
			),
		},
	];

	const eligibleForPayout = (device: Device) => {
		if (device.funding_type !== 'zazu_funded') {
			return false;
		}

		if (!device.sold_by) {
			return false;
		}

		if (parseInt(device.phone_amount) - parseInt(device.cash_in_hand) < parseInt(device.zf_paid_to_retailer)) {
			return false;
		}

		return true;
	};

	const handleFilterApply = (values: FilterValues) => {
		setFilters(values);
		setPaginationConfig({
			...paginationConfig,
			page: 1, // Reset to first page when filtering
		});
	};

	const handleFilterReset = () => {
		form.resetFields();
		setFilters({
			status: null,
			phone_number: null,
			retailer: null,
			user: null,
			date_range: null,
		});
		setPaginationConfig({
			...paginationConfig,
			page: 1,
		});
	};

	return (
		<AppLayout
			title="Reports/Device Report v1"
			subtitle="Device Report v1"
		>
			<Card className="mb-4 shadow-sm">
				<Form
					form={form}
					layout="horizontal"
					onFinish={handleFilterApply}
					initialValues={filters}
				>
					<div className="flex flex-wrap items-end gap-4">
						<Form.Item
							name="phone_number"
							label="Phone Number"
							className="mb-0 min-w-[180px]"
						>
							<Input
								placeholder="Enter phone number"
								allowClear
							/>
						</Form.Item>

						<Form.Item
							name="retailer"
							label="Retailer ID"
							className="mb-0 min-w-[150px]"
						>
							<Input
								placeholder="Retailer ID"
								type="number"
								allowClear
							/>
						</Form.Item>

						<Form.Item
							name="user"
							label="User ID"
							className="mb-0 min-w-[150px]"
						>
							<Input
								placeholder="User ID"
								type="number"
								allowClear
							/>
						</Form.Item>

						<Form.Item
							name="date_range"
							label="Date Range"
							className="mb-0 min-w-[300px]"
						>
							<RangePicker
								allowClear
								format="YYYY-MM-DD"
								placeholder={['Start Date', 'End Date']}
							/>
						</Form.Item>

						<Form.Item className="mb-0 flex-shrink-0">
							<Space>
								<Button
									type="primary"
									htmlType="submit"
									icon={<FilterOutlined />}
								>
									Apply Filters
								</Button>
								<Button
									onClick={handleFilterReset}
									icon={<ReloadOutlined />}
								>
									Reset
								</Button>
							</Space>
						</Form.Item>
					</div>
				</Form>
			</Card>

			<div className="mb-20">
				<Table
					columns={columns}
					dataSource={devices}
					rowKey="id"
					pagination={{
						total: count,
						pageSize: paginationConfig.pageSize,
						current: paginationConfig.page,
						showSizeChanger: true,
						showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
						onChange: (page, pageSize) => {
							setPaginationConfig({ page, pageSize });
						},
					}}
					loading={isLoading}
				/>
			</div>

			{selectedDevice && (
				<DeviceDetails
					device={selectedDevice}
					onClose={() => setSelectedDevice(null)}
				/>
			)}

			{selectedPayoutDevice && (
				<RetailerPayout
					device={selectedPayoutDevice}
					open
					onClose={() => setSelectedPayoutDevice(null)}
				/>
			)}
		</AppLayout>
	);
};

export default DeviceReportV1;
